import React, { useState } from 'react';
import { BentoCard } from '@study-tracker/ui';
import { IDatabase } from '@study-tracker/data';
import { StudentProfile, Subject } from '@study-tracker/domain';
import { SUBJECT_CHOICES } from '../subjectChoices';

interface FirstRunSetupScreenProps {
  db: IDatabase;
  profile: StudentProfile;
  subjects: Subject[];
  onComplete: (profile: StudentProfile) => void;
}

export const FirstRunSetupScreen: React.FC<FirstRunSetupScreenProps> = ({ db, profile, subjects, onComplete }) => {
  const [name, setName] = useState('');
  const [primaryCode, setPrimaryCode] = useState('');
  const [additionalCode, setAdditionalCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const fullName = name.trim();
    const chosenCodes = [primaryCode, additionalCode];
    if (!fullName || chosenCodes.some((code) => !code)) {
      setError('Enter your name and choose both subjects to continue.');
      return;
    }

    const states = SUBJECT_CHOICES.flatMap((pair, index) => {
      const selectedCode = chosenCodes[index];
      return pair.choices.flatMap((choice) => {
        const subject = subjects.find((candidate) => candidate.code === choice.code);
        return subject ? [{ id: subject.id, isArchived: choice.code !== selectedCode }] : [];
      });
    });
    if (states.length !== SUBJECT_CHOICES.length * 2) {
      setError('Some subject choices are missing from local storage. Reload the app and try again.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await db.setSubjectArchivedStates(states);
      const updatedProfile = await db.studentProfile.upsert({
        ...profile,
        fullName,
        candidateId: '',
        stream: 'G.C.E. Advanced Level',
        bio: 'G.C.E. Advanced Level student',
      });
      onComplete(updatedProfile);
    } catch (saveError: unknown) {
      console.error('First-run setup failed:', saveError);
      setError(`Could not save your setup: ${saveError instanceof Error ? saveError.message : String(saveError)}`);
    } finally {
      setSaving(false);
    }
  };

  return <main className="min-h-screen bg-[#0E0E0E] px-4 py-8 text-[#E5E2E1]">
    <div className="mx-auto flex min-h-[80vh] max-w-xl flex-col justify-center gap-5">
      <div>
        <p className="font-mono text-xs tracking-widest text-al-primary">[ A/L CORE ] · FIRST-TIME SETUP</p>
        <h1 className="mt-3 font-display text-3xl font-bold">Make this tracker yours</h1>
        <p className="mt-2 text-sm text-al-on-surface-variant">Your profile and study records stay on this device unless you choose to sync them.</p>
      </div>
      <BentoCard elevation="standard" className="p-5 sm:p-7">
        <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-5">
          <label className="text-sm text-al-on-surface-variant">
            Your name
            <input
              autoComplete="name"
              autoFocus
              maxLength={120}
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Enter the name you want shown in the app"
              className="mt-2 w-full rounded-xl border border-al-outline-variant bg-al-surface-low p-3 text-base text-al-on-surface"
            />
          </label>
          {SUBJECT_CHOICES.map((pair, index) => {
            const value = index === 0 ? primaryCode : additionalCode;
            const setValue = index === 0 ? setPrimaryCode : setAdditionalCode;
            return <label key={pair.title} className="text-sm text-al-on-surface-variant">
              Choose your {pair.title.toLocaleLowerCase()}
              <select
                required
                value={value}
                onChange={(event) => setValue(event.target.value)}
                className="mt-2 w-full rounded-xl border border-al-outline-variant bg-al-surface-low p-3 text-base text-al-on-surface"
              >
                <option value="" disabled>Select a subject</option>
                {pair.choices.map((choice) => {
                  const exists = subjects.some((subject) => subject.code === choice.code);
                  return <option key={choice.code} value={choice.code} disabled={!exists}>{choice.label}</option>;
                })}
              </select>
            </label>;
          })}
          {error && <p role="alert" className="rounded-xl border border-al-error/40 bg-al-error-container/20 p-3 text-sm text-al-error">{error}</p>}
          <button type="submit" disabled={saving} className="rounded-full bg-al-primary px-5 py-3 text-sm font-semibold text-al-on-primary disabled:opacity-50">
            {saving ? 'Saving your setup…' : 'Start studying'}
          </button>
        </form>
      </BentoCard>
    </div>
  </main>;
};
